import { Injectable } from '@angular/core';
import { LogFiltersType } from '@shared/services/logs/logs.service';
import { Filter, FilterDefinitions, FiltersEnum, FilterType, initData } from '../../../theme/my-logs/filters/filters';
import _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class FilterBridgeService {
  constructor() {}

  public encodeFilter(filter: Filter): string | null {
    const key = filter.filterDefinition?.key;
    if (!key) return null;

    const body = this.encodeFilterBody(filter);
    if (body === null) return null;

    const activePrefix = filter.active === false ? '!' : '';
    return `${activePrefix}${key}:${body}`;
  }

  encodeFilterBody(filter: Filter): string | null {
    switch (filter.filterDefinition.type) {
      case FilterType.MULTI_IN_NOTIN: {
        const inVals = (filter.data?.[LogFiltersType.IN] ?? []) as Array<{ value: any }>;
        const notInVals = (filter.data?.[LogFiltersType.NOTIN] ?? []) as Array<{ value: any }>;
        const segments: string[] = [];
        if (inVals.length) segments.push(`i=${inVals.map((v) => encodeURIComponent(v.value)).join(',')}`);
        if (notInVals.length) segments.push(`n=${notInVals.map((v) => encodeURIComponent(v.value)).join(',')}`);
        return segments.length ? segments.join(';') : null;
      }

      case FilterType.SINGLE_INPUT:
      case FilterType.SINGLE_SELECT: {
        const v = filter.data;
        if (v === null || v === undefined || v === '') return null;
        return `v=${encodeURIComponent(String(v))}`;
      }

      case FilterType.INTERVAL: {
        const gte = filter.data?.[LogFiltersType.GTE];
        const lte = filter.data?.[LogFiltersType.LTE];
        const segments: string[] = [];
        if (gte !== undefined && gte !== null && gte !== '') segments.push(`g=${encodeURIComponent(String(gte))}`);
        if (lte !== undefined && lte !== null && lte !== '') segments.push(`l=${encodeURIComponent(String(lte))}`);
        return segments.length ? segments.join(';') : null;
      }

      default:
        return null;
    }
  }

  decodeFilter(chunk: string, defs: FilterDefinitions): Filter | null {
    if (!chunk) return null;

    let active = true;
    let rest = chunk;
    if (rest.startsWith('!')) {
      active = false;
      rest = rest.slice(1);
    }

    const colonIdx = rest.indexOf(':');
    if (colonIdx === -1) return null;

    const key = rest.slice(0, colonIdx) as FiltersEnum;
    const body = rest.slice(colonIdx + 1);

    const filterDefinition = defs[key];
    if (!filterDefinition) return null;

    const data = this.decodeFilterBody(body, filterDefinition.type, filterDefinition.choices);
    if (data === null) return null;

    return { active, data, filterDefinition };
  }
  decodeFilterKey(chunk: string): FiltersEnum | null {
    if (!chunk) return null;
    const rest = chunk.startsWith('!') ? chunk.slice(1) : chunk;
    const colonIdx = rest.indexOf(':');
    if (colonIdx === -1) return null;
    return rest.slice(0, colonIdx) as FiltersEnum;
  }
  decodeFilterBody(body: string, type: FilterType, choices?: Array<{ slug: string; value: any }>): any {
    const map = new Map<string, string>();
    for (const seg of body.split(';').filter(Boolean)) {
      const eq = seg.indexOf('=');
      if (eq === -1) continue;
      map.set(seg.slice(0, eq), seg.slice(eq + 1));
    }

    const slugFor = (value: any): string => {
      if (!choices) return String(value);
      const found = choices.find((c) => String(c.value) === String(value));
      return found?.slug ?? String(value);
    };

    switch (type) {
      case FilterType.MULTI_IN_NOTIN: {
        const data = _.cloneDeep(initData[type]);
        const parseList = (raw: string | undefined) => {
          if (!raw) return [];
          return raw
            .split(',')
            .filter(Boolean)
            .map((v) => {
              const decoded = decodeURIComponent(v);
              return { slug: slugFor(decoded), value: decoded };
            });
        };
        data[LogFiltersType.IN] = parseList(map.get('i'));
        data[LogFiltersType.NOTIN] = parseList(map.get('n'));
        if (!data[LogFiltersType.IN].length && !data[LogFiltersType.NOTIN].length) return null;
        return data;
      }

      case FilterType.SINGLE_INPUT: {
        const v = map.get('v');
        return v !== undefined ? decodeURIComponent(v) : null;
      }

      case FilterType.SINGLE_SELECT: {
        const v = map.get('v');
        if (v === undefined) return null;
        const decoded = decodeURIComponent(v);
        if (decoded === 'true') return true;
        if (decoded === 'false') return false;
        const asNum = Number(decoded);
        return Number.isFinite(asNum) && decoded.trim() !== '' ? asNum : decoded;
      }

      case FilterType.INTERVAL: {
        const data = _.cloneDeep(initData[type]);
        const g = map.get('g');
        const l = map.get('l');
        if (g !== undefined) data[LogFiltersType.GTE] = decodeURIComponent(g);
        if (l !== undefined) data[LogFiltersType.LTE] = decodeURIComponent(l);
        if (data[LogFiltersType.GTE] === undefined && data[LogFiltersType.LTE] === undefined) return null;
        return data;
      }

      default:
        return null;
    }
  }
}
